/**
 * Industry-Specific Training Programs Data
 * Based on market research of in-demand training programs in India for 2025
 * Each industry has specific training programs tailored to their unique requirements
 */

import {
  Factory,
  Monitor,
  GraduationCap,
  Hospital,
  Building2,
  Truck,
  Settings,
  Award,
  Users,
  Shield,
  TrendingUp,
  Target,
  Zap,
  Lightbulb,
  CheckCircle,
  BookOpen,
  Briefcase,
  Activity
} from 'lucide-react';

export const industryTrainings = [
  {
    id: 'manufacturing',
    slug: 'manufacturing',
    icon: Factory,
    name: 'Manufacturing',
    title: 'Manufacturing Industry Training Programs',
    description: 'Comprehensive quality, operational excellence, and safety training programs designed specifically for manufacturing professionals',
    heroImage: 'https://images.unsplash.com/photo-1754372069872-3fef8a815c87?w=1200&q=80',
    overview: 'Transform your manufacturing operations with industry-leading training in quality management, lean manufacturing, Six Sigma, and workplace safety. Our programs are designed to improve productivity, enhance quality, and ensure compliance with international standards.',

    benefits: [
      'Improve operational efficiency by 10-30%',
      'Reduce waste and manufacturing costs',
      'Enhance product quality and consistency',
      'Achieve international quality certifications',
      'Build a culture of continuous improvement',
      'Ensure workplace safety and compliance'
    ],

    targetAudience: [
      'Production Managers & Supervisors',
      'Quality Assurance Teams',
      'Maintenance Engineers',
      'Process Engineers',
      'Plant Managers',
      'Operations Managers'
    ],

    trainings: [
      {
        id: 'lean-six-sigma',
        slug: 'lean-six-sigma-training',
        customPath: '/Lean-Six-Sigma-Certification-Course',
        canonicalUrl: 'https://isde.academy/Lean-Six-Sigma-Certification-Course',
        icon: TrendingUp,
        title: 'Lean Six Sigma Training',
        shortDescription: 'Master process improvement and waste reduction methodologies',
        duration: '3-5 days',
        level: 'Beginner to Advanced',
        description: 'Comprehensive training in Lean Six Sigma methodologies including DMAIC, statistical process control, and process optimization. Learn to reduce waste, improve quality, and drive operational excellence.',

        keyTopics: [
          'DMAIC Methodology (Define, Measure, Analyze, Improve, Control)',
          'Statistical Process Control (SPC)',
          'Process Capability Analysis',
          'Root Cause Analysis & Problem Solving',
          'Value Stream Mapping',
          'Waste Reduction Strategies',
          'Control Charts & Quality Tools',
          'Project Management for Six Sigma'
        ],

        learningOutcomes: [
          'Achieve 10-30% improvement in productivity',
          'Reduce process variation and defects',
          'Lead Six Sigma improvement projects',
          'Implement data-driven decision making',
          'Earn globally recognized Six Sigma certification'
        ],

        certificationLevels: ['Yellow Belt', 'Green Belt', 'Black Belt'],
        deliveryMode: ['Classroom', 'Online', 'Hybrid'],

        metaTitle: 'Lean Six Sigma Training in India | Yellow Belt to Black Belt Certification',
        metaDescription: 'Master Lean Six Sigma methodologies with industry-expert training. Achieve 10-30% productivity improvement. Yellow Belt, Green Belt, Black Belt certifications available.',
        metaKeywords: 'lean six sigma training india, six sigma certification, DMAIC, process improvement, quality management'
      },

      {
        id: '5s-training',
        slug: '5s-training-chennai',
        icon: Settings,
        title: '5S Training in Chennai',
        shortDescription: 'Professional 5S training for workplace organization, efficiency, and safety in Chennai industries',
        duration: '1-2 days',
        level: 'All Levels',
        description: 'In today\'s competitive business environment, organizations must continuously improve productivity, quality, and safety while reducing waste and inefficiencies. Professional 5S training in Chennai helps organizations create a clean, organized, and standardized workplace that supports efficiency, safety, and continuous improvement. With Chennai emerging as a major hub for manufacturing, automotive, electronics, healthcare, logistics, and IT services, 5S Training in Chennai has become essential for operational excellence.',

        keyTopics: [
          'Introduction to 5S and Lean Thinking',
          'Seiri (Sort) - Red tag methodology and removing clutter',
          'Seiton (Set in Order) - Visual storage, labeling, shadow boards',
          'Seiso (Shine) - Cleaning as inspection, equipment care',
          'Seiketsu (Standardize) - SOPs, visual standards, checklists',
          'Shitsuke (Sustain) - Building discipline, 5S audits, ownership',
          'Visual Management and Color Coding Systems',
          'Workplace Layout Planning',
          '5S Implementation and Performance Monitoring'
        ],

        learningOutcomes: [
          'Improved workplace organization and efficiency',
          'Reduction in waste, rework, and downtime',
          'Enhanced safety and reduced accidents',
          'Better space utilization and quality',
          'Faster retrieval of tools and materials',
          'Strong foundation for Lean, TPM, and Kaizen',
          'Improved employee ownership and morale',
          'Visible improvements within short period'
        ],

        deliveryMode: ['Classroom', 'On-Site'],

        targetAudience: [
          'Top management and leadership teams',
          'Production and operations staff',
          'Quality and maintenance teams',
          'Supervisors and team leaders',
          'Office and administrative staff',
          'Warehouse and logistics personnel',
          'All employees across shop floor and office'
        ],

        industriesServed: [
          'Automotive and auto components',
          'Manufacturing and fabrication',
          'Electronics and electrical industries',
          'Healthcare and hospitals',
          'Warehousing and logistics',
          'IT and corporate offices',
          'Food processing and packaging'
        ],

        benefits: [
          'Experienced 5S consultants with hands-on implementation',
          'Practical and results-driven training approach',
          'Customized programs based on industry and maturity',
          'End-to-end support from awareness to sustainment',
          'High ROI with visible improvements',
          'Tailored for Chennai industries and operations'
        ],

        metaTitle: '5S Training in Chennai | Workplace Organization & Safety | ISDE Academy',
        metaDescription: 'Professional 5S Training in Chennai for manufacturing, automotive, healthcare, IT industries. Master workplace organization, efficiency, safety. Experienced trainers, customized programs.',
        metaKeywords: '5S training Chennai, workplace organization Chennai, 5S methodology, lean manufacturing Chennai, kaizen training, 5S consultants Chennai'
      },

      {
        id: 'iso-9001-manufacturing',
        slug: 'iso-9001-qms-training-manufacturing',
        icon: Award,
        title: 'ISO 9001 Quality Management System',
        shortDescription: 'Quality management system implementation for manufacturing excellence',
        duration: '1-5 days',
        level: 'Awareness to Lead Auditor',
        description: 'Complete training in ISO 9001:2015 Quality Management System tailored for manufacturing industries. From awareness to lead auditor certification.',

        keyTopics: [
          'ISO 9001:2015 Requirements',
          'Quality Management Principles',
          'Process Approach to QMS',
          'Risk-Based Thinking',
          'Internal Auditing Techniques',
          'Documentation & Records Management',
          'Continual Improvement',
          'Customer Satisfaction Measurement'
        ],

        learningOutcomes: [
          'Implement ISO 9001 QMS in manufacturing',
          'Conduct internal quality audits',
          'Achieve ISO 9001 certification',
          'Improve product quality consistency',
          'Enhance customer satisfaction'
        ],

        certificationLevels: ['Awareness', 'Internal Auditor', 'Lead Auditor'],
        deliveryMode: ['Classroom', 'Online', 'Hybrid'],

        metaTitle: 'ISO 9001 Training for Manufacturing | QMS Certification in India',
        metaDescription: 'ISO 9001:2015 Quality Management System training for manufacturing. Awareness, Internal Auditor, Lead Auditor courses. Achieve quality certification.',
        metaKeywords: 'ISO 9001 training manufacturing, quality management system, QMS certification india'
      },

      {
        id: 'core-tools-automotive',
        slug: 'automotive-core-tools-training-apqp-ppap-fmea',
        icon: Target,
        title: 'Automotive Core Tools (APQP, PPAP, FMEA, MSA, SPC)',
        shortDescription: 'Master IATF 16949 core tools for automotive quality',
        duration: '3-5 days',
        level: 'Intermediate to Advanced',
        description: 'Comprehensive training in automotive industry core tools required by IATF 16949. Learn APQP, PPAP, FMEA, MSA, and SPC methodologies essential for automotive suppliers.',

        keyTopics: [
          'APQP (Advanced Product Quality Planning)',
          'PPAP (Production Part Approval Process)',
          'FMEA (Failure Mode & Effects Analysis)',
          'MSA (Measurement System Analysis)',
          'SPC (Statistical Process Control)',
          'Control Plans',
          'Problem Solving (8D, 5 Why)',
          'IATF 16949 Requirements'
        ],

        learningOutcomes: [
          'Implement automotive quality requirements',
          'Conduct FMEA and risk assessments',
          'Prepare PPAP documentation',
          'Apply statistical quality control',
          'Meet automotive customer requirements'
        ],

        deliveryMode: ['Classroom', 'Hybrid', 'Corporate'],

        metaTitle: 'Automotive Core Tools Training | APQP PPAP FMEA MSA SPC in India',
        metaDescription: 'Master automotive core tools - APQP, PPAP, FMEA, MSA, SPC. IATF 16949 compliant training for automotive suppliers. Expert-led certification courses.',
        metaKeywords: 'automotive core tools training, APQP PPAP FMEA, IATF 16949, automotive quality india'
      },

      {
        id: 'fmea-manufacturing',
        slug: 'fmea-failure-mode-effects-analysis-manufacturing',
        customPath: '/FMEA-Training',
        canonicalUrl: 'https://isde.academy/FMEA-Training',
        icon: Target,
        title: 'FMEA - Failure Mode & Effects Analysis',
        shortDescription: 'Master risk identification and mitigation through systematic FMEA',
        duration: '2-3 days',
        level: 'Intermediate to Advanced',
        description: 'Comprehensive training in Failure Mode & Effects Analysis (FMEA). Learn DFMEA, PFMEA, and SFMEA methodologies for risk identification, assessment, and mitigation in manufacturing.',

        keyTopics: [
          'FMEA Fundamentals & Methodology',
          'Design FMEA (DFMEA) - Risk prevention in design phase',
          'Process FMEA (PFMEA) - Risk prevention in production',
          'System FMEA (SFMEA) - Risk at system level',
          'Severity, Occurrence, Detection (SOD) Analysis',
          'Risk Priority Number (RPN) Calculation',
          'Control Plans & Preventive Actions',
          'FMEA Documentation & Best Practices',
          'Integration with Quality Management Systems'
        ],

        learningOutcomes: [
          'Conduct systematic FMEA across design and process phases',
          'Identify potential failures before occurrence',
          'Develop robust control and preventive measures',
          'Reduce product recalls and customer complaints',
          'Meet industry and customer FMEA requirements',
          'Implement risk-based quality improvements'
        ],

        certificationLevels: ['Practitioner', 'Advanced Practitioner'],
        deliveryMode: ['Classroom', 'Hybrid', 'Corporate'],

        metaTitle: 'FMEA Training | Failure Mode & Effects Analysis for Manufacturing',
        metaDescription: 'Master FMEA methodology - DFMEA, PFMEA, SFMEA. Learn risk identification, RPN calculation, control plans. Industry-expert certification training.',
        metaKeywords: 'FMEA training manufacturing, failure mode effects analysis, DFMEA PFMEA, risk management, quality tools'
      },

      {
        id: 'core-tools-general',
        slug: 'core-tools-training-manufacturing',
        customPath: '/Core-Tools-Training',
        canonicalUrl: 'https://isde.academy/Core-Tools-Training',
        icon: Target,
        title: 'Core Tools Training (APQP, PPAP, MSA, SPC, FMEA)',
        shortDescription: 'Master essential quality core tools for manufacturing excellence',
        duration: '4-5 days',
        level: 'Intermediate to Advanced',
        description: 'Comprehensive training in core quality tools essential for manufacturing industries. Learn APQP, PPAP, MSA, SPC, and FMEA - methodologies required for automotive, aerospace, medical device, and machinery manufacturers.',

        keyTopics: [
          'APQP (Advanced Product Quality Planning) - 5-phase approach',
          'PPAP (Production Part Approval Process) - Documentation & submission',
          'FMEA (Failure Mode & Effects Analysis) - Risk assessment',
          'MSA (Measurement System Analysis) - Gauge capability & variation',
          'SPC (Statistical Process Control) - Process monitoring & control',
          'Control Plans & Process Documentation',
          'Problem Solving & Root Cause Analysis',
          'Integration of Core Tools in QMS',
          'Industry-Specific Applications'
        ],

        learningOutcomes: [
          'Master all five core tools comprehensively',
          'Plan, design, and approve new products & processes',
          'Ensure measurement system reliability',
          'Monitor and control processes statistically',
          'Prevent failures through systematic risk analysis',
          'Meet customer and industry compliance requirements',
          'Lead core tool initiatives in manufacturing'
        ],

        certificationLevels: ['Core Tools Certified Professional'],
        deliveryMode: ['Classroom', 'Hybrid', 'Corporate'],

        metaTitle: 'Core Tools Training | APQP PPAP FMEA MSA SPC Certification',
        metaDescription: 'Master core quality tools - APQP, PPAP, FMEA, MSA, SPC. Essential training for automotive, aerospace, medical, machinery manufacturers. Expert certification.',
        metaKeywords: 'core tools training, APQP PPAP FMEA MSA SPC, quality management, manufacturing excellence, IATF 16949, AS9100, ISO 13485'
      },

      {
        id: 'workplace-safety',
        slug: 'safety-first-aid-training',
        icon: Shield,
        title: 'Safety and First Aid Training',
        shortDescription: 'Essential life skills for preventing accidents, managing risks, and providing immediate emergency care',
        duration: '1-2 days',
        level: 'All Levels',
        description: 'Safety and first aid training are no longer optional. They are essential life skills in today\'s fast-paced and risk-prone world. Whether in workplaces, schools, healthcare facilities, construction sites, or homes, emergencies can occur at any time. Having trained individuals who can respond quickly and correctly can mean the difference between life and death. Safety and first aid training equips individuals with the knowledge and confidence to prevent accidents, manage risks, and provide immediate care during emergencies until professional medical help arrives.',

        keyTopics: [
          'Cuts, wounds, and bleeding control',
          'Burns and scalds',
          'Fractures and sprains',
          'Choking management',
          'Shock treatment',
          'CPR and AED Training',
          'Emergency Response to Fires',
          'Electrical Accidents',
          'Chemical Spills',
          'Hazard Recognition',
          'Personal Protective Equipment (PPE)',
          'Ergonomic Safety',
          'Fire Safety and Prevention',
          'Hand Hygiene Practices',
          'Infection Control'
        ],

        learningOutcomes: [
          'Identify potential hazards and prevent accidents',
          'Provide immediate first aid care during emergencies',
          'Perform CPR and use AED equipment effectively',
          'Respond to workplace emergencies with confidence',
          'Ensure compliance with safety regulations',
          'Build a safety-first culture in your organization'
        ],

        deliveryMode: ['Classroom', 'On-Site', 'Hands-On Practice'],
        certification: 'First Aid & Safety Certification',
        geographicArea: 'India',

        metaTitle: 'Safety & First Aid Training Courses | Certified Workplace Training',
        metaDescription: 'Enroll in certified Safety and First Aid Training courses for workplaces. Learn emergency response, CPR, and injury prevention from expert trainers.',
        metaKeywords: 'safety and first aid training, CPR training india, first aid certification, workplace safety training chennai, emergency response training, AED training, first aid course india, safety training bangalore, certified first aid training',
        canonicalUrl: 'https://isde.academy/safety-first-aid-training'
      },

      {
        id: 'tpm-training',
        slug: 'total-productive-maintenance-course',
        customPath: '/total-productive-maintenance-course',
        canonicalUrl: 'https://isde.academy/total-productive-maintenance-course',
        icon: Activity,
        title: 'Total Productive Maintenance Course',
        shortDescription: 'Strategic TPM training to maximize equipment effectiveness, improve OEE, and achieve operational excellence',
        duration: '2-3 days',
        level: 'All Levels',
        description: 'In today\'s highly competitive industrial environment, equipment reliability and operational efficiency play a critical role in business success. Unplanned breakdowns, low equipment availability, and poor maintenance practices lead to production losses, quality issues, and increased operating costs. This Total Productive Maintenance course helps organizations develop a proactive maintenance culture, improve Overall Equipment Effectiveness (OEE), and achieve operational excellence. TPM is a structured maintenance methodology that aims to maximize equipment effectiveness by involving everyone in the organization, from top management to shop-floor operators.',

        keyTopics: [
          'TPM Concepts and Principles',
          'Eight Pillars of TPM Implementation',
          'Autonomous Maintenance (Operator Ownership)',
          'Planned and Preventive Maintenance',
          'Overall Equipment Effectiveness (OEE) Measurement',
          'Focused Improvement (Kaizen) for Equipment',
          'Quality Maintenance and Defect Prevention',
          'Early Equipment Management and Design',
          'Data-Driven Approaches for OEE Improvement',
          'Creating Sustainable TPM Culture'
        ],

        learningOutcomes: [
          'Increased equipment availability and reliability',
          'Reduced unplanned downtime and breakdowns',
          'Lower maintenance costs through prevention',
          'Improved productivity and manufacturing output',
          'Enhanced workplace safety and compliance',
          'Improved product quality and consistency',
          'Empowered operators with maintenance skills',
          'Ability to lead TPM initiatives and improvements'
        ],

        deliveryMode: ['Classroom', 'On-Site'],

        targetAudience: [
          'Production managers and engineers',
          'Maintenance managers and technicians',
          'Quality professionals',
          'Plant and operations heads',
          'Supervisors and team leaders',
          'Lean and continuous improvement teams',
          'Operators and shop-floor personnel',
          'All levels across organization'
        ],

        industriesServed: [
          'Manufacturing and fabrication',
          'Automotive and auto components',
          'Electronics and electrical industries',
          'Food processing and packaging',
          'Pharmaceuticals and healthcare',
          'Power plants and utilities',
          'Logistics and warehousing',
          'Process industries'
        ],

        organizationalBenefits: [
          'Increased equipment availability',
          'Reduced unplanned downtime',
          'Lower maintenance costs',
          'Improved productivity and output',
          'Enhanced safety and compliance',
          'Improved product quality'
        ],

        individualBenefits: [
          'Strong understanding of TPM principles',
          'Practical maintenance and improvement skills',
          'Ability to lead TPM initiatives',
          'Career growth in operations and maintenance',
          'Recognized professional certification'
        ],

        courseFeatures: [
          'Experienced TPM trainers and industry experts',
          'Practical, implementation-focused training',
          'Customized programs for different industries',
          'End-to-end support from training to execution',
          'Proven results across multiple sectors',
          'Real, sustainable improvements delivered'
        ],

        metaTitle: 'Total Productive Maintenance Course | TPM Training & OEE Improvement | ISDE Academy',
        metaDescription: 'Professional Total Productive Maintenance (TPM) Course for manufacturing excellence. Master 8 pillars of TPM, OEE improvement, autonomous maintenance. Reduce downtime, increase equipment effectiveness.',
        metaKeywords: 'total productive maintenance course, TPM training, TPM certification, OEE improvement, autonomous maintenance, preventive maintenance, equipment effectiveness, manufacturing excellence'
      },

      {
        id: 'statistical-tools-quality',
        slug: 'statistical-tools-for-quality-management-training',
        icon: TrendingUp,
        title: 'Statistical Tools for Quality Management',
        shortDescription: 'Master SPC, QC tools, data analysis, and process improvement using statistical methods for quality excellence',
        duration: '3-5 days',
        level: 'Beginner to Advanced',
        description: 'In today\'s competitive business environment, quality is no longer based on assumptions or experience alone. Organizations across manufacturing, healthcare, education, laboratories, and service sectors rely on data to improve processes, reduce defects, and ensure customer satisfaction. Statistical tools enable quality professionals to analyze data objectively, identify root causes, monitor performance, and implement continuous improvement initiatives. Training in these tools equips participants with practical, hands-on skills that support quality management systems such as ISO 9001, ISO 13485, ISO 17025, Six Sigma, Lean, and Total Quality Management (TQM).',

        keyTopics: [
          'Check Sheets - Data collection and defect recording',
          'Histograms - Frequency distribution and process variation',
          'Pareto Charts - 80/20 principle for prioritizing quality issues',
          'Cause-and-Effect Diagram (Fishbone/Ishikawa) - Root cause analysis',
          'Control Charts - Statistical Process Control (SPC)',
          'Run Charts - Trend and shift detection over time',
          'Scatter Diagrams - Correlation and variable relationships',
          'Process Capability Analysis (Cp, Cpk indices)',
          'Regression Analysis - Predictive data-based decision making',
          'Design of Experiments (DOE) - Multi-variable optimization'
        ],

        learningOutcomes: [
          'Understand process variation and its impact on quality',
          'Identify quality problems and trends using statistical methods',
          'Make data-driven decisions with confidence',
          'Improve process stability and capability',
          'Support continuous improvement and Six Sigma initiatives',
          'Apply SPC tools for real-time process monitoring',
          'Conduct root cause analysis using structured tools',
          'Evaluate process capability using Cp and Cpk indices'
        ],

        importance: [
          'Enables data-driven decision making over guesswork',
          'Improves process control and early defect detection',
          'Supports compliance with ISO 9001, ISO 13485, ISO 17025',
          'Strengthens continuous improvement culture',
          'Enhances professional competence and career growth'
        ],

        statisticalTools: [
          {
            name: 'Check Sheets',
            description: 'Simple but powerful data collection tools used to record defects, errors, or events in real time.',
            focus: ['Designing effective check sheets', 'Collecting accurate data', 'Categorizing defects']
          },
          {
            name: 'Histograms',
            description: 'Displays the frequency distribution of data, helping visualize process variation.',
            focus: ['Creating histograms', 'Interpreting data distribution', 'Identifying patterns and spread']
          },
          {
            name: 'Pareto Charts',
            description: 'Follows the 80/20 principle, highlighting the few critical causes responsible for most problems.',
            focus: ['Constructing Pareto charts', 'Prioritizing quality issues', 'Identifying improvement opportunities']
          },
          {
            name: 'Cause-and-Effect Diagram',
            description: 'Also known as the Ishikawa or Fishbone diagram, helps identify potential root causes of a problem.',
            focus: ['Structuring causes under Man, Machine, Method, Material, Measurement, Environment', 'Conducting team-based root cause analysis']
          },
          {
            name: 'Control Charts',
            description: 'Essential tools used to monitor process performance over time in SPC and Six Sigma projects.',
            focus: ['Understanding common-cause and special-cause variation', 'Creating and interpreting control charts', 'Maintaining process stability']
          },
          {
            name: 'Run Charts',
            description: 'Displays data points over time to identify trends, shifts, or cycles.',
            focus: ['Plotting data sequentially', 'Detecting trends and variations', 'Understanding short-term process behavior']
          },
          {
            name: 'Scatter Diagrams',
            description: 'Shows the relationship between two variables and helps identify correlations.',
            focus: ['Plotting and interpreting scatter diagrams', 'Understanding correlation versus causation', 'Supporting hypothesis testing']
          },
          {
            name: 'Process Capability Analysis',
            description: 'Measures how well a process meets customer or specification requirements.',
            focus: ['Understanding Cp and Cpk indices', 'Interpreting capability results', 'Identifying improvement needs']
          },
          {
            name: 'Regression Analysis',
            description: 'Examines the relationship between dependent and independent variables.',
            focus: ['Basic regression concepts', 'Predicting outcomes', 'Identifying key influencing factors']
          },
          {
            name: 'Design of Experiments (DOE)',
            description: 'Advanced statistical method used to study multiple variables simultaneously.',
            focus: ['Planning experiments', 'Analyzing results', 'Identifying optimal process conditions']
          }
        ],

        targetAudience: [
          'Quality managers and engineers',
          'Internal and lead auditors',
          'Process improvement professionals',
          'Manufacturing and operations teams',
          'Healthcare and laboratory professionals',
          'Six Sigma and Lean practitioners',
          'Students and fresh graduates in quality disciplines'
        ],

        benefits: [
          'Improved understanding of process behavior',
          'Reduced defects and rework',
          'Better root cause identification',
          'Enhanced process stability and capability',
          'Stronger compliance with quality standards',
          'Increased employee confidence and competence'
        ],

        applicableStandards: ['ISO 9001', 'ISO 13485', 'ISO 17025', 'Six Sigma', 'Lean', 'TQM'],
        deliveryMode: ['Classroom', 'Online', 'Hybrid'],
        certification: 'Statistical Quality Tools Certification',

        metaTitle: 'Statistical Tools for Quality Management Training | SPC & QC',
        metaDescription: 'Learn statistical tools for quality management training covering SPC, QC tools, data analysis & process improvement with certification.',
        metaKeywords: 'statistical tools quality management, SPC training, quality control tools, process capability, control charts, pareto analysis, fishbone diagram, six sigma tools, quality management certification'
      },

      {
        id: 'design-of-experiments',
        slug: 'design-of-experiments-training',
        icon: Lightbulb,
        title: 'Design of Experiments Training',
        shortDescription: 'Optimize processes, improve quality, and reduce variation using practical, data-driven DOE methods',
        duration: '3-5 days',
        level: 'Intermediate to Advanced',
        description: 'Design of Experiments training is a structured learning program that teaches participants how to plan, conduct, analyze, and interpret experiments using statistical principles. The training focuses on practical application rather than complex theory, enabling participants to make data-driven decisions confidently. DOE is widely used in Six Sigma, Lean manufacturing, quality engineering, R&D, healthcare, laboratories, and service industries.',

        keyTopics: [
          'Experimental Design Principles - Factors, levels, and responses',
          'Controlled vs. Uncontrolled Variables',
          'Randomization, Replication, and Blocking',
          'Full Factorial Designs - All combinations of factors and levels',
          'Fractional Factorial Designs - Efficient screening of significant factors',
          'Screening Experiments - Identifying most influential factors',
          'Interaction Effects - Detection and interpretation',
          'Response Surface Methodology (RSM) - Process optimization',
          'Analysis of Variance (ANOVA) - Hypothesis testing and p-values',
          'Robust Design - Reducing process sensitivity to variation'
        ],

        learningOutcomes: [
          'Identify critical process variables and their interactions',
          'Plan and conduct structured experiments efficiently',
          'Reduce process variation with fewer trials',
          'Optimize performance using data-driven methods',
          'Apply Full Factorial and Fractional Factorial designs',
          'Use Response Surface Methodology for process optimization',
          'Interpret ANOVA results and validate conclusions',
          'Implement robust design for process stability'
        ],

        importance: [
          'Replaces trial-and-error with scientific methods',
          'Improves process understanding and factor interactions',
          'Reduces cost and time through fewer experiments',
          'Enhances product and process quality and consistency',
          'Supports Six Sigma DMAIC and Lean improvement projects'
        ],

        doeTypes: [
          {
            name: 'Full Factorial Designs',
            description: 'Studies all possible combinations of factors and levels for complete interaction analysis.',
            focus: ['All factor combinations', 'Complete interaction information', 'Detailed analysis']
          },
          {
            name: 'Fractional Factorial Designs',
            description: 'Uses fewer experiments for efficient screening of significant factors.',
            focus: ['Fewer experiments needed', 'Screening significant factors', 'Resource-efficient']
          },
          {
            name: 'Screening Experiments',
            description: 'Identifies the most influential factors among many variables.',
            focus: ['Focus on critical inputs', 'Eliminate insignificant factors', 'Simplify further experimentation']
          },
          {
            name: 'Response Surface Methodology (RSM)',
            description: 'Advanced technique for building predictive models and optimizing processes.',
            focus: ['Predictive models', 'Optimal operating conditions', 'Response surface visualization']
          },
          {
            name: 'Robust Design',
            description: 'Makes processes less sensitive to variation for improved stability.',
            focus: ['Improved process stability', 'Reduced defects', 'Higher customer satisfaction']
          }
        ],

        targetAudience: [
          'Quality engineers and managers',
          'Six Sigma Green Belts and Black Belts',
          'Manufacturing and process engineers',
          'R&D and product development teams',
          'Laboratory professionals',
          'Operations and continuous improvement teams'
        ],

        benefits: [
          'Data-driven decision-making',
          'Reduced experimental cost and time',
          'Improved process capability',
          'Better root cause identification',
          'Enhanced innovation and problem-solving skills',
          'Stronger quality and productivity outcomes'
        ],

        applications: [
          'Manufacturing process optimization',
          'Product design and development',
          'Quality improvement initiatives',
          'Healthcare and laboratory testing',
          'Service process improvement'
        ],

        careerBenefits: [
          'Higher analytical credibility',
          'Better project outcomes',
          'Strong demand in quality and process roles',
          'Enhanced career growth and leadership potential'
        ],

        deliveryMode: ['Classroom', 'Online', 'Hybrid'],
        certification: 'DOE Certification',

        metaTitle: 'Design of Experiments Training | DOE Certification Course',
        metaDescription: 'Learn Design of Experiments (DOE) training to optimize processes, improve quality & reduce variation using practical, data-driven methods.',
        metaKeywords: 'design of experiments training, DOE certification, factorial design, response surface methodology, ANOVA, six sigma DOE, process optimization, quality improvement, DOE course'
      },

      {
        id: '8d-problem-solving',
        slug: '8d-training',
        icon: Target,
        title: '8D Problem Solving Training',
        shortDescription: 'Master the Eight Disciplines methodology to resolve complex problems, eliminate defects, and drive continuous improvement',
        duration: '2-3 days',
        level: 'All Levels',
        description: '8D Problem-Solving Training is a structured learning program that teaches participants how to apply the Eight Disciplines methodology to resolve complex problems. Originally developed by Ford Motor Company, the 8D methodology is now widely used across industries as a standard problem-solving and corrective action framework. The training emphasizes teamwork, discipline, and data-driven decision-making to identify root causes, implement corrective actions, and prevent recurrence.',

        keyTopics: [
          'D1: Establish the Team - Cross-functional team formation, roles, and responsibilities',
          'D2: Define the Problem - Problem statements, 5W2H analysis, and impact assessment',
          'D3: Implement Interim Containment Actions - Risk containment and verification',
          'D4: Identify Root Causes - 5 Why analysis, Fishbone diagrams, and data validation',
          'D5: Develop Permanent Corrective Actions - Solution brainstorming and feasibility evaluation',
          'D6: Implement and Validate Corrective Actions - Action planning and effectiveness monitoring',
          'D7: Prevent Recurrence - System-level improvements, SOPs, and lessons learned',
          'D8: Recognize the Team - Knowledge sharing and continuous improvement culture'
        ],

        learningOutcomes: [
          'Identify and define problems clearly using structured methods',
          'Analyze root causes using data-driven techniques',
          'Implement effective corrective actions that eliminate root causes',
          'Prevent recurrence of issues through system-level improvements',
          'Improve customer satisfaction and quality performance',
          'Lead cross-functional 8D problem-solving teams',
          'Apply 8D methodology aligned with ISO 9001, IATF 16949, and AS9100'
        ],

        importance: [
          'Structured, step-by-step approach ensures problems are addressed systematically',
          'Focuses on identifying true root causes instead of treating symptoms',
          'Ensures solutions are sustainable and prevent recurrence',
          'Prioritizes customer protection and satisfaction',
          'Aligned with ISO 9001, IATF 16949, ISO 13485, and AS9100 standards'
        ],

        targetAudience: [
          'Quality managers and engineers',
          'Manufacturing and process engineers',
          'Internal and lead auditors',
          'Supplier quality professionals',
          'Operations and production teams',
          'Continuous improvement and Six Sigma practitioners'
        ],

        benefits: [
          'Improved root cause identification',
          'Reduced repeat problems and defects',
          'Faster and more effective problem resolution',
          'Enhanced customer satisfaction',
          'Stronger corrective and preventive action systems',
          'Better teamwork and communication',
          'Improved compliance with quality standards'
        ],

        applications: [
          'Manufacturing and automotive industries',
          'Aerospace and defense',
          'Medical devices and healthcare',
          'Electronics and engineering',
          'Supplier quality management'
        ],

        deliveryMode: ['Classroom', 'Online', 'Hybrid'],
        certification: '8D Problem Solving Certification',

        metaTitle: '8D Problem Solving Training | 8D Methodology Course',
        metaDescription: 'Hands-on 8D problem solving training for quality and manufacturing teams to eliminate defects and drive continuous improvement.',
        metaKeywords: '8D training, 8D problem solving, 8D methodology, root cause analysis, corrective action, CAPA, quality management, 8D certification, Ford 8D, manufacturing problem solving'
      }
    ]
  },

  {
    id: 'information-technology',
    slug: 'information-technology',
    icon: Monitor,
    name: 'Information Technology',
    title: 'IT & ITES Industry Training Programs',
    description: 'Cutting-edge training in IT service management, information security, agile methodologies, and cybersecurity',
    heroImage: 'https://images.unsplash.com/flagged/photo-1579274216947-86eaa4b00475?w=1200&q=80',
    overview: 'Stay ahead in the rapidly evolving IT landscape with training in ITIL, ISO 27001, DevOps, Agile, and cybersecurity. Our programs align with global standards and prepare professionals for digital transformation challenges.',

    benefits: [
      'Master modern IT service management frameworks',
      'Achieve globally recognized IT certifications',
      'Enhance information security practices',
      'Implement agile and DevOps methodologies',
      'Advance career in IT industry',
      'Meet compliance and regulatory requirements'
    ],

    targetAudience: [
      'IT Managers & Team Leads',
      'System Administrators',
      'Security Professionals',
      'DevOps Engineers',
      'Project Managers',
      'IT Consultants'
    ],

    trainings: [
      {
        id: 'itil-4-foundation',
        slug: 'itil-4-foundation-certification-training',
        icon: BookOpen,
        title: 'ITIL 4 Foundation Certification',
        shortDescription: 'Master IT service management with ITIL 4 framework',
        duration: '3 days',
        level: 'Foundation',
        description: 'ITIL 4 Foundation certification training aligned with modern ITSM practices, digital transformation, and service value optimization. Essential for IT professionals in 2025.',

        keyTopics: [
          'ITIL 4 Service Value System',
          'Four Dimensions of Service Management',
          'Service Value Chain',
          'ITIL Practices (34 Management Practices)',
          'Continual Improvement',
          'Digital Transformation & DevOps Integration',
          'Value Co-creation',
          'Service Relationship Management'
        ],

        learningOutcomes: [
          'Understand ITIL 4 framework and principles',
          'Implement modern ITSM practices',
          'Integrate ITIL with Agile and DevOps',
          'Pass ITIL 4 Foundation exam',
          'Optimize IT service delivery'
        ],

        certification: 'ITIL 4 Foundation (PeopleCert)',
        deliveryMode: ['Online', 'Classroom', 'Hybrid'],
        examIncluded: true,

        metaTitle: 'ITIL 4 Foundation Certification Training in India | Online & Classroom',
        metaDescription: 'ITIL 4 Foundation certification training for IT professionals. Master modern ITSM practices, service value chain, DevOps integration. PeopleCert certified.',
        metaKeywords: 'ITIL 4 foundation training india, ITIL certification, IT service management, ITSM training'
      },

      {
        id: 'iso-27001-isms',
        slug: 'iso-27001-information-security-training',
        icon: Shield,
        title: 'ISO 27001:2022 Information Security Management',
        shortDescription: 'Implement robust information security and cybersecurity practices',
        duration: '1-5 days',
        level: 'Awareness to Lead Auditor',
        description: 'Comprehensive ISO 27001:2022 training covering Information Security, Cybersecurity, and Privacy Protection. Essential for organizations handling sensitive data.',

        keyTopics: [
          'ISO 27001:2022 Requirements',
          'Information Security Risk Assessment',
          'Cybersecurity Controls & Measures',
          'Privacy Protection (GDPR alignment)',
          'Security Incident Management',
          'Business Continuity Planning',
          'Internal Auditing of ISMS',
          'Compliance & Certification Process'
        ],

        learningOutcomes: [
          'Implement ISO 27001 ISMS',
          'Conduct security risk assessments',
          'Achieve ISO 27001 certification',
          'Enhance cybersecurity posture',
          'Meet regulatory compliance requirements'
        ],

        certificationLevels: ['Awareness', 'Internal Auditor', 'Lead Auditor', 'Lead Implementer'],
        deliveryMode: ['Online', 'Classroom', 'Hybrid'],

        metaTitle: 'ISO 27001:2022 Training in India | Information Security Certification',
        metaDescription: 'ISO 27001:2022 Information Security Management training. Learn cybersecurity, privacy protection, ISMS implementation. Lead Auditor certification available.',
        metaKeywords: 'ISO 27001 training india, information security certification, ISMS, cybersecurity training'
      },

      {
        id: 'devops-agile',
        slug: 'devops-agile-training-certification',
        icon: Zap,
        title: 'DevOps & Agile Methodologies',
        shortDescription: 'Master modern software development and deployment practices',
        duration: '3-5 days',
        level: 'Intermediate to Advanced',
        description: 'Comprehensive training in DevOps practices, CI/CD pipelines, and Agile methodologies. Learn to accelerate software delivery and improve collaboration.',

        keyTopics: [
          'DevOps Culture & Principles',
          'Continuous Integration/Continuous Deployment (CI/CD)',
          'Infrastructure as Code (IaC)',
          'Container Technology (Docker, Kubernetes)',
          'Agile Scrum Framework',
          'Sprint Planning & Execution',
          'Automated Testing & Quality Assurance',
          'Monitoring & Logging'
        ],

        learningOutcomes: [
          'Implement DevOps culture and practices',
          'Build CI/CD pipelines',
          'Master Agile Scrum methodologies',
          'Accelerate software delivery',
          'Improve team collaboration'
        ],

        certificationOptions: ['Certified Scrum Master', 'DevOps Foundation'],
        deliveryMode: ['Online', 'Classroom', 'Hybrid'],

        metaTitle: 'DevOps & Agile Training in India | CI/CD, Scrum Certification',
        metaDescription: 'Master DevOps and Agile methodologies. Learn CI/CD, Docker, Kubernetes, Scrum framework. Accelerate software delivery with expert-led training.',
        metaKeywords: 'devops training india, agile scrum certification, CI/CD training, kubernetes docker'
      },

      {
        id: 'cybersecurity-training',
        slug: 'cybersecurity-awareness-training',
        icon: Shield,
        title: 'Cybersecurity Awareness & Best Practices',
        shortDescription: 'Build strong cybersecurity culture across your organization',
        duration: '1-2 days',
        level: 'All Levels',
        description: 'Essential cybersecurity training for all IT professionals and employees. Learn to identify threats, protect data, and respond to security incidents.',

        keyTopics: [
          'Common Cyber Threats & Attack Vectors',
          'Phishing & Social Engineering',
          'Password Security & Authentication',
          'Data Protection & Encryption',
          'Secure Email & Web Browsing',
          'Mobile Device Security',
          'Incident Response Basics',
          'Regulatory Compliance (GDPR, IT Act)'
        ],

        learningOutcomes: [
          'Identify and prevent cyber threats',
          'Protect sensitive information',
          'Practice secure computing habits',
          'Respond to security incidents',
          'Build security-aware culture'
        ],

        deliveryMode: ['Online', 'Classroom'],
        certification: 'Cybersecurity Awareness Certificate',

        metaTitle: 'Cybersecurity Awareness Training in India | IT Security Best Practices',
        metaDescription: 'Essential cybersecurity awareness training for organizations. Learn threat identification, data protection, incident response. Build security-first culture.',
        metaKeywords: 'cybersecurity training india, cyber security awareness, data protection training'
      },

      {
        id: 'prince2-project-management',
        slug: 'prince2-project-management-certification',
        icon: Briefcase,
        title: 'PRINCE2 Project Management',
        shortDescription: 'Structured project management methodology for IT projects',
        duration: '3-5 days',
        level: 'Foundation to Practitioner',
        description: 'PRINCE2 (Projects IN Controlled Environments) certification training - globally recognized project management methodology widely used in IT industry.',

        keyTopics: [
          'PRINCE2 Principles, Themes & Processes',
          'Business Case Development',
          'Risk Management in Projects',
          'Change Control',
          'Quality Management',
          'Project Organization Structure',
          'Progress Monitoring & Control',
          'Tailoring PRINCE2 for Projects'
        ],

        learningOutcomes: [
          'Manage IT projects effectively',
          'Apply PRINCE2 methodology',
          'Pass PRINCE2 certification exam',
          'Improve project success rates',
          'Advance PM career opportunities'
        ],

        certificationLevels: ['Foundation', 'Practitioner'],
        deliveryMode: ['Online', 'Classroom', 'Hybrid'],
        examIncluded: true,

        metaTitle: 'PRINCE2 Certification Training in India | Project Management Course',
        metaDescription: 'PRINCE2 Foundation and Practitioner certification training for IT professionals. Master structured project management methodology. Exam included.',
        metaKeywords: 'PRINCE2 training india, project management certification, PRINCE2 foundation practitioner'
      },

      {
        id: 'ai-training-program',
        slug: 'ai-training-program',
        icon: Lightbulb,
        title: 'AI Training Program',
        shortDescription: 'Master artificial intelligence, machine learning, deep learning, and data-driven decision making',
        duration: '3-5 days',
        level: 'Beginner to Advanced',
        description: 'An AI Training Program is a structured learning course designed to teach the principles, technologies, and applications of artificial intelligence. These programs focus on enabling learners to analyze data, build intelligent models, automate processes, and make data-driven decisions using AI techniques. AI training programs combine theory with hands-on practice to ensure practical competence.',

        keyTopics: [
          'Fundamentals of Artificial Intelligence',
          'Machine Learning and Deep Learning',
          'Data Analysis and Model Development',
          'AI Tools, Platforms, and Programming',
          'Supervised and Unsupervised Learning',
          'Neural Networks (CNNs, RNNs)',
          'Natural Language Processing (NLP) Basics',
          'Real-world Applications and Case Studies'
        ],

        learningOutcomes: [
          'Understand AI principles and how it works',
          'Build predictive models using machine learning',
          'Apply deep learning techniques for complex problems',
          'Work with AI tools and industry-relevant platforms',
          'Analyze data and develop intelligent solutions',
          'Implement AI solutions for business problems',
          'Earn AI training certification validating your skills'
        ],

        importance: [
          'Rapid adoption of AI across industries',
          'High demand and global skill shortage for AI professionals',
          'Business innovation and competitive advantage',
          'Automation and digital transformation',
          'Career resilience in a changing job market'
        ],

        keyComponents: [
          'Conceptual learning with strong theoretical understanding',
          'Hands-on projects with real-world datasets',
          'Industry use cases across healthcare, finance, marketing',
          'Assessment through assignments, quizzes, and projects',
          'Recognized certification upon completion'
        ],

        targetAudience: [
          'Students and fresh graduates',
          'IT and software professionals',
          'Data analysts and data scientists',
          'Engineers and technical professionals',
          'Business analysts and managers',
          'Healthcare and finance professionals',
          'Entrepreneurs and startup founders'
        ],

        benefits: [
          'Career advancement with high-paying opportunities',
          'Cross-industry applicability and career flexibility',
          'Improved problem-solving and analytical skills',
          'Business impact through efficiency and scalability',
          'Future-proof skill set as technology evolves'
        ],

        careerOpportunities: [
          'AI Engineer',
          'Machine Learning Engineer',
          'Data Scientist',
          'AI Analyst',
          'Business Intelligence Analyst',
          'AI Consultant',
          'Automation Specialist'
        ],

        futureScope: [
          'Generative AI and large language models',
          'Responsible and ethical AI',
          'AI in healthcare and life sciences',
          'AI-driven automation and robotics',
          'AI integration with IoT and cloud computing'
        ],

        choosingCriteria: [
          'Curriculum aligned with industry needs',
          'Hands-on projects and practical exposure',
          'Experienced trainers and mentors',
          'Use of real-world datasets',
          'Recognized certification',
          'Career support and guidance'
        ],

        deliveryMode: ['Online', 'Classroom', 'Hybrid'],
        certification: 'AI Training Certification',

        metaTitle: 'AI Training Program | Artificial Intelligence Certification Course',
        metaDescription: 'Advance your career with an AI Training Program. Learn machine learning, AI models, automation, and industry use cases.',
        metaKeywords: 'AI training program, artificial intelligence certification, machine learning training, deep learning course, AI certification india, artificial intelligence course'
      }
    ]
  },

  {
    id: 'aerospace',
    slug: 'aerospace',
    icon: Zap,
    name: 'Aerospace & Defense',
    title: 'Aerospace & Defense Industry Training Programs',
    description: 'Specialized training for AS9100 compliance, quality management, and safety in aerospace and defense manufacturing',
    heroImage: 'https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=1200&q=80',
    overview: 'Master aerospace and defense standards including AS9100, EN 9100, and NADCAP requirements. Our programs focus on quality excellence, safety, and regulatory compliance essential for aerospace suppliers and manufacturers.',

    benefits: [
      'Achieve AS9100 and EN 9100 certification',
      'Meet NADCAP audit requirements',
      'Implement aerospace-grade quality systems',
      'Ensure foreign object debris (FOD) prevention',
      'Comply with configuration management requirements',
      'Meet first-article inspection (FAI) standards'
    ],

    targetAudience: [
      'Aerospace Quality Engineers',
      'Manufacturing Supervisors',
      'Process Engineers',
      'Quality Managers',
      'Compliance Officers',
      'Design Engineers'
    ],

    trainings: [
      {
        id: 'as9100-training',
        slug: 'as9100-aerospace-quality-management-system-training',
        icon: Award,
        title: 'AS9100 Quality Management System Training',
        shortDescription: 'AS9100 compliance and aerospace quality management',
        duration: '3-5 days',
        level: 'Awareness to Lead Auditor',
        description: 'Comprehensive training in AS9100:2016 (Rev D) aerospace quality management standard. Learn design, development, manufacturing, and maintenance requirements specific to aerospace industry.',

        keyTopics: [
          'AS9100 Scope and Requirements',
          'Configuration Management',
          'Foreign Object Debris (FOD) Prevention',
          'Product Safety, Traceability & Counterfeit Parts',
          'Risk Management in Aerospace',
          'First Article Inspection (FAI)',
          'Supply Chain Management',
          'Documentation & Records',
          'Internal Auditing for AS9100'
        ],

        learningOutcomes: [
          'Implement AS9100 quality management system',
          'Meet NADCAP audit requirements',
          'Ensure configuration and traceability',
          'Prevent counterfeit parts entry',
          'Conduct AS9100 internal audits',
          'Achieve AS9100 certification'
        ],

        certificationLevels: ['Awareness', 'Internal Auditor', 'Lead Auditor'],
        deliveryMode: ['Classroom', 'Online', 'Hybrid'],

        metaTitle: 'AS9100 Training | Aerospace Quality Management System Certification',
        metaDescription: 'AS9100:2016 Rev D training for aerospace quality management. FOD prevention, configuration management, NADCAP compliance. Expert-led certification.',
        metaKeywords: 'AS9100 training, aerospace quality management, NADCAP, FOD prevention, configuration management'
      },

      {
        id: 'fmea-aerospace',
        slug: 'fmea-aerospace-critical-applications-training',
        icon: Target,
        title: 'FMEA for Aerospace & Critical Applications',
        shortDescription: 'Advanced FMEA for high-reliability aerospace systems',
        duration: '3 days',
        level: 'Advanced',
        description: 'Specialized FMEA training for aerospace and high-reliability applications. Learn DFMEA and PFMEA with focus on safety-critical systems, failure analysis, and risk mitigation.',

        keyTopics: [
          'FMEA Fundamentals for Aerospace',
          'Design FMEA (DFMEA) for Safety-Critical Systems',
          'Process FMEA (PFMEA) with Aerospace Rigor',
          'Severity Assessment for Aerospace',
          'Single Point of Failure Analysis',
          'Critical Item Management',
          'Failure Analysis & Prevention',
          'Control & Preventive Measures',
          'AS9100 Integration with FMEA'
        ],

        learningOutcomes: [
          'Conduct aerospace-grade FMEA',
          'Identify and mitigate critical failures',
          'Ensure system reliability and safety',
          'Meet aerospace customer requirements',
          'Implement risk-based quality measures',
          'Lead FMEA initiatives in aerospace'
        ],

        deliveryMode: ['Classroom', 'Hybrid'],

        metaTitle: 'FMEA for Aerospace | Critical Systems Risk Analysis Training',
        metaDescription: 'Advanced FMEA for aerospace and critical systems. Safety-critical analysis, single point of failure detection, AS9100 integration.',
        metaKeywords: 'FMEA aerospace, failure analysis, critical systems, reliability, safety-critical applications'
      },

      {
        id: 'core-tools-aerospace',
        slug: 'core-tools-aerospace-quality-excellence-training',
        icon: Target,
        title: 'Core Tools for Aerospace (APQP, PPAP, MSA, SPC, FMEA)',
        shortDescription: 'Master core quality tools for aerospace manufacturing',
        duration: '5 days',
        level: 'Intermediate to Advanced',
        description: 'Comprehensive core tools training tailored for aerospace manufacturing. Master APQP, PPAP, MSA, SPC, and FMEA with focus on aerospace-specific requirements and AS9100 compliance.',

        keyTopics: [
          'APQP with Aerospace Configuration Management',
          'PPAP with First Article Inspection (FAI)',
          'FMEA with Critical Item Identification',
          'MSA for Precision Aerospace Components',
          'SPC with Aerospace Control Plans',
          'Foreign Object Debris (FOD) Prevention',
          'Supply Chain Quality Assurance',
          'Counterfeit Parts Prevention',
          'AS9100 Integration'
        ],

        learningOutcomes: [
          'Master all core tools for aerospace',
          'Plan and approve aerospace products',
          'Ensure precision measurement systems',
          'Control critical processes statistically',
          'Prevent failures in safety-critical systems',
          'Meet AS9100 and customer requirements',
          'Lead aerospace quality initiatives'
        ],

        deliveryMode: ['Classroom', 'Hybrid', 'Corporate'],

        metaTitle: 'Core Tools for Aerospace | APQP PPAP FMEA MSA SPC Training',
        metaDescription: 'Core quality tools for aerospace manufacturing - APQP, PPAP, FMEA, MSA, SPC. AS9100 compliant, FAI, FOD prevention training.',
        metaKeywords: 'core tools aerospace, APQP PPAP, FMEA aerospace, AS9100, aerospace quality management'
      }
    ]
  },

  {
    id: 'medical-devices',
    slug: 'medical-devices',
    icon: Activity,
    name: 'Medical Device Manufacturing',
    title: 'Medical Device Industry Training Programs',
    description: 'Comprehensive training for ISO 13485 compliance, risk management, and quality systems in medical device manufacturing',
    heroImage: 'https://images.unsplash.com/photo-1579154204601-01d82b44f81d?w=1200&q=80',
    overview: 'Master ISO 13485 and medical device quality management systems. Our training programs focus on regulatory compliance, risk management, and quality excellence required by FDA, EMA, and other regulatory bodies.',

    benefits: [
      'Achieve ISO 13485 certification',
      'Ensure FDA and EMA compliance',
      'Implement risk management (ISO 14971)',
      'Manage design controls effectively',
      'Ensure traceability and record management',
      'Build quality into every process'
    ],

    targetAudience: [
      'Quality Managers',
      'Regulatory Affairs Specialists',
      'Design Engineers',
      'Manufacturing Engineers',
      'Quality Assurance Professionals',
      'Compliance Officers'
    ],

    trainings: [
      {
        id: 'iso-13485-training',
        slug: 'iso-13485-medical-device-quality-management-training',
        icon: Award,
        title: 'ISO 13485 for Medical Device Manufacturing',
        shortDescription: 'Complete QMS training for medical device manufacturers, designers, and suppliers',
        duration: '3-5 days',
        level: 'Awareness to Lead Auditor',
        description: 'Comprehensive ISO 13485:2016 training specifically designed for medical device manufacturers and product development teams. Master the complete quality management system requirements including FDA 21 CFR Part 820, EU MDR compliance, design controls, and product lifecycle management. This course covers everything from initial concept through post-market surveillance.',

        keyTopics: [
          'ISO 13485:2016 Clause-by-Clause Interpretation',
          'FDA 21 CFR Part 820 Integration',
          'EU MDR & IVDR Compliance Requirements',
          'Design Controls & Design History File (DHF)',
          'Device Master Record (DMR) Management',
          'Risk Management Integration (ISO 14971)',
          'Supplier & Contract Manufacturer Controls',
          'Sterilization & Cleanroom Requirements',
          'Process Validation (IQ, OQ, PQ)',
          'CAPA & Nonconforming Product Management',
          'Post-Market Surveillance & Vigilance'
        ],

        learningOutcomes: [
          'Build ISO 13485 compliant QMS from scratch',
          'Navigate FDA and EU MDR regulatory requirements',
          'Create and manage Design History Files (DHF)',
          'Implement design controls throughout product development',
          'Establish supplier qualification programs',
          'Lead internal audits and management reviews',
          'Prepare for third-party certification audits'
        ],

        certificationLevels: ['Awareness', 'Internal Auditor', 'Lead Auditor'],
        deliveryMode: ['Classroom', 'Online', 'Hybrid'],
        targetAudience: ['Quality Engineers', 'Design Engineers', 'Regulatory Affairs', 'Manufacturing Managers', 'R&D Teams'],

        metaTitle: 'ISO 13485 Training for Medical Device Manufacturers | QMS Certification India',
        metaDescription: 'ISO 13485:2016 training for medical device manufacturers. FDA 21 CFR Part 820, EU MDR compliance, design controls, DHF management, supplier quality. Lead Auditor certification.',
        metaKeywords: 'ISO 13485 manufacturer training, medical device QMS, FDA 21 CFR 820, EU MDR, design controls, DHF, medical device certification india'
      },

      {
        id: 'fmea-medical',
        slug: 'fmea-medical-device-risk-management-training',
        icon: Target,
        title: 'FMEA & Risk Management for Medical Devices (ISO 14971)',
        shortDescription: 'FMEA and risk management for medical device safety',
        duration: '2-3 days',
        level: 'Intermediate to Advanced',
        description: 'Specialized FMEA and risk management training for medical device development. Learn ISO 14971 risk management principles, FMEA application, and hazard analysis for patient safety.',

        keyTopics: [
          'ISO 14971 Risk Management Framework',
          'Hazard Analysis & Identification',
          'Design FMEA (DFMEA) for Medical Devices',
          'Risk Assessment & Evaluation',
          'Residual Risk Analysis',
          'Risk Control Measures',
          'Risk-Benefit Analysis',
          'Biocompatibility & Safety Risks',
          'Post-Market Surveillance & Risk Monitoring'
        ],

        learningOutcomes: [
          'Implement ISO 14971 risk management',
          'Conduct comprehensive FMEA',
          'Identify device-related hazards',
          'Develop risk control strategies',
          'Ensure patient safety',
          'Meet regulatory requirements',
          'Lead risk management activities'
        ],

        deliveryMode: ['Classroom', 'Hybrid'],

        metaTitle: 'FMEA & Risk Management Medical Devices | ISO 14971 Training',
        metaDescription: 'FMEA and ISO 14971 risk management for medical devices. Hazard analysis, risk control, patient safety, regulatory compliance.',
        metaKeywords: 'FMEA medical devices, ISO 14971, risk management, hazard analysis, patient safety'
      },

      {
        id: 'core-tools-medical',
        slug: 'core-tools-medical-device-quality-management-training',
        icon: Target,
        title: 'Core Tools for Medical Devices (APQP, MSA, SPC, FMEA)',
        shortDescription: 'Core quality tools for medical device manufacturing',
        duration: '4-5 days',
        level: 'Intermediate to Advanced',
        description: 'Comprehensive core tools training specifically designed for medical device manufacturing. Master APQP, MSA, SPC, and FMEA with focus on ISO 13485 and regulatory compliance.',

        keyTopics: [
          'APQP for Medical Device Development',
          'Design History File (DHF) Creation',
          'FMEA with ISO 14971 Integration',
          'MSA for Medical Device Components',
          'SPC for Process Control',
          'Control Plans for Medical Devices',
          'Design of Experiments (DoE)',
          'Validation & Verification',
          'Post-Market Surveillance'
        ],

        learningOutcomes: [
          'Master core tools for medical device QMS',
          'Plan and develop medical devices',
          'Ensure precision manufacturing',
          'Control critical processes',
          'Manage product risks effectively',
          'Meet ISO 13485 & FDA requirements',
          'Lead quality improvement initiatives'
        ],

        deliveryMode: ['Classroom', 'Hybrid', 'Corporate'],

        metaTitle: 'Core Tools Medical Devices | APQP MSA SPC FMEA Training',
        metaDescription: 'Core quality tools for medical device manufacturing - APQP, MSA, SPC, FMEA. ISO 13485 compliant, regulatory training.',
        metaKeywords: 'core tools medical devices, APQP MSA SPC FMEA, ISO 13485, FDA compliance'
      }
    ]
  },

  {
    id: 'healthcare',
    slug: 'healthcare',
    icon: Hospital,
    name: 'Healthcare',
    title: 'Healthcare Industry Training Programs',
    description: 'Medical device quality, healthcare management, and patient safety training programs',
    heroImage: 'https://images.pexels.com/photos/40568/medical-appointment-doctor-healthcare-40568.jpeg?w=1200&q=80',
    overview: 'Comprehensive training programs for healthcare organizations focusing on quality management, medical device regulations, patient safety, and healthcare accreditation standards including ISO 13485, NABH, and regulatory compliance.',

    benefits: [
      'Achieve medical device quality certification',
      'Ensure patient safety and care quality',
      'Meet regulatory compliance requirements',
      'Obtain NABH accreditation',
      'Improve healthcare service delivery',
      'Build quality-focused healthcare culture'
    ],

    targetAudience: [
      'Quality Managers (Healthcare)',
      'Medical Device Manufacturers',
      'Regulatory Affairs Specialists',
      'Hospital Administrators',
      'Clinical Staff',
      'Healthcare Auditors'
    ],

    trainings: [
      {
        id: 'iso-13485',
        slug: 'iso-13485-medical-devices-qms-training',
        icon: Award,
        title: 'ISO 13485 for Healthcare Facilities & Procurement',
        shortDescription: 'Medical device quality assessment for hospitals, clinics, and healthcare procurement teams',
        duration: '1-3 days',
        level: 'Awareness to Internal Auditor',
        description: 'Specialized ISO 13485 training designed for healthcare facilities, hospital administrators, and biomedical engineering teams. Learn how to evaluate medical device suppliers, ensure equipment quality, manage device lifecycle in clinical settings, and comply with CDSCO and ICMED requirements. This course focuses on the healthcare facility perspective of medical device quality management.',

        keyTopics: [
          'ISO 13485 Overview for Healthcare Facilities',
          'Medical Device Procurement & Vendor Assessment',
          'CDSCO Registration & Compliance Requirements',
          'ICMED 13485 Certification Scheme (India)',
          'Equipment Installation & Validation',
          'Biomedical Equipment Maintenance Standards',
          'Device Incident Reporting & Vigilance',
          'Medical Equipment Lifecycle Management',
          'Supplier Quality Auditing for Healthcare',
          'Integration with Hospital Quality Systems (NABH)'
        ],

        learningOutcomes: [
          'Evaluate medical device supplier quality systems',
          'Ensure CDSCO and ICMED compliance for procurement',
          'Implement device maintenance and calibration programs',
          'Manage medical equipment lifecycle in hospitals',
          'Report device incidents and adverse events properly',
          'Integrate device quality with NABH accreditation',
          'Conduct supplier quality audits'
        ],

        certificationLevels: ['Awareness', 'Internal Auditor'],
        deliveryMode: ['Classroom', 'Online', 'Hybrid'],
        regulatoryContext: ['CDSCO India', 'ICMED', 'NABH Integration'],
        targetAudience: ['Hospital Administrators', 'Biomedical Engineers', 'Procurement Teams', 'Quality Managers', 'Clinical Engineers'],

        metaTitle: 'ISO 13485 Training for Hospitals & Healthcare Facilities | ICMED CDSCO Compliance India',
        metaDescription: 'ISO 13485 training for healthcare facilities and hospitals. Medical device procurement, CDSCO compliance, ICMED certification, equipment management. Designed for hospital quality teams.',
        metaKeywords: 'ISO 13485 hospital training, healthcare facility medical devices, ICMED certification, CDSCO compliance, medical equipment management india'
      },

      {
        id: 'nabh-accreditation',
        slug: 'nabh-accreditation-hospital-training',
        icon: Hospital,
        title: 'NABH Accreditation for Hospitals',
        shortDescription: 'Quality accreditation standards for healthcare organizations',
        duration: '3-5 days',
        level: 'Hospital Management & Quality Teams',
        description: 'Complete training for NABH (National Accreditation Board for Hospitals & Healthcare Providers) accreditation. Achieve excellence in healthcare quality and patient safety.',

        keyTopics: [
          'NABH Standards for Hospitals',
          'Patient-Centered Care',
          'Patient Rights & Education',
          'Infection Control & Prevention',
          'Medication Management',
          'Hospital Information System',
          'Quality Improvement Programs',
          'Accreditation Process & Documentation'
        ],

        learningOutcomes: [
          'Understand NABH standards',
          'Implement quality patient care',
          'Prepare for NABH assessment',
          'Achieve NABH accreditation',
          'Improve patient safety outcomes'
        ],

        deliveryMode: ['Classroom', 'On-Site', 'Hybrid'],
        accreditationLevel: ['Entry Level', 'Full Accreditation'],

        metaTitle: 'NABH Accreditation Training for Hospitals in India | Healthcare Quality',
        metaDescription: 'Complete NABH accreditation training for hospitals and healthcare providers. Learn quality standards, patient safety, accreditation process. Achieve NABH certification.',
        metaKeywords: 'NABH accreditation training india, hospital quality standards, healthcare accreditation'
      },

      {
        id: 'patient-safety',
        slug: 'patient-safety-quality-care-training',
        icon: Shield,
        title: 'Patient Safety & Quality Care',
        shortDescription: 'Essential training for patient safety and quality healthcare delivery',
        duration: '2-3 days',
        level: 'All Healthcare Staff',
        description: 'Comprehensive patient safety training covering error prevention, incident reporting, risk management, and quality care protocols for all healthcare professionals.',

        keyTopics: [
          'Patient Safety Principles',
          'Adverse Event Prevention',
          'Medication Safety',
          'Infection Prevention & Control',
          'Patient Identification Protocols',
          'Incident Reporting & Root Cause Analysis',
          'Communication & Handover',
          'Safety Culture Development'
        ],

        learningOutcomes: [
          'Prevent medical errors and adverse events',
          'Implement safety protocols',
          'Conduct incident investigations',
          'Improve patient care quality',
          'Build safety-conscious culture'
        ],

        deliveryMode: ['Classroom', 'Online', 'On-Site'],
        mandatoryFor: 'All Healthcare Providers',

        metaTitle: 'Patient Safety Training in India | Quality Healthcare & Error Prevention',
        metaDescription: 'Essential patient safety and quality care training for healthcare professionals. Learn error prevention, incident reporting, safety protocols. Build safer healthcare.',
        metaKeywords: 'patient safety training india, healthcare quality, medical error prevention'
      },

      {
        id: 'clinical-research',
        slug: 'clinical-research-gcp-training',
        icon: Lightbulb,
        title: 'Clinical Research & Good Clinical Practice (GCP)',
        shortDescription: 'Training in clinical trials and ethical research practices',
        duration: '3-5 days',
        level: 'Researchers & Clinical Staff',
        description: 'Comprehensive training in clinical research methodology and Good Clinical Practice (GCP) guidelines. Essential for conducting ethical and compliant clinical trials.',

        keyTopics: [
          'ICH-GCP Guidelines',
          'Clinical Trial Design & Phases',
          'Regulatory Requirements (CDSCO, ICMR)',
          'Ethics Committee & Informed Consent',
          'Protocol Development',
          'Data Management & Safety Monitoring',
          'Adverse Event Reporting',
          'Clinical Trial Documentation'
        ],

        learningOutcomes: [
          'Conduct ethical clinical trials',
          'Comply with GCP guidelines',
          'Manage clinical research data',
          'Obtain regulatory approvals',
          'Ensure patient protection in research'
        ],

        certification: 'GCP Certification',
        deliveryMode: ['Classroom', 'Online'],
        regulatoryBodies: ['CDSCO', 'ICMR', 'ICH'],

        metaTitle: 'Clinical Research & GCP Training in India | ICH-GCP Certification',
        metaDescription: 'Clinical research and Good Clinical Practice (GCP) training. Learn clinical trial design, regulatory compliance, ethics. ICH-GCP certification for researchers.',
        metaKeywords: 'clinical research training india, GCP certification, ICH-GCP, clinical trials'
      },

      {
        id: 'healthcare-waste-management',
        slug: 'biomedical-waste-management-training',
        icon: Activity,
        title: 'Biomedical Waste Management',
        shortDescription: 'Safe handling and disposal of healthcare waste',
        duration: '1-2 days',
        level: 'All Hospital Staff',
        description: 'Training on Biomedical Waste Management Rules 2016 compliance. Learn safe handling, segregation, treatment, and disposal of biomedical waste.',

        keyTopics: [
          'BMW Rules 2016 (India)',
          'Waste Segregation & Color Coding',
          'Collection & Storage',
          'Treatment & Disposal Methods',
          'Occupational Safety',
          'Waste Minimization',
          'Record Keeping & Reporting',
          'Emergency Procedures'
        ],

        learningOutcomes: [
          'Comply with BMW Rules 2016',
          'Segregate waste properly',
          'Ensure safe waste handling',
          'Protect healthcare workers',
          'Minimize environmental impact'
        ],

        deliveryMode: ['Classroom', 'On-Site'],
        mandatoryCompliance: 'BMW Rules 2016',

        metaTitle: 'Biomedical Waste Management Training in India | BMW Rules 2016 Compliance',
        metaDescription: 'Biomedical waste management training for healthcare facilities. Learn BMW Rules 2016 compliance, segregation, disposal. Ensure safety and environmental protection.',
        metaKeywords: 'biomedical waste management training india, BMW rules 2016, healthcare waste disposal'
      }
    ]
  },

  {
    id: 'corporate-services',
    slug: 'corporate-services',
    icon: Building2,
    name: 'Corporate Training',
    title: 'Corporate Training Courses in India',
    description: 'Industry-focused corporate training programs covering leadership, technical skills, compliance & performance improvement',
    heroImage: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1200&q=80',
    overview: 'A comprehensive corporate training program in India covers a wide range of learning areas from Business English and Behavioral Training to Leadership Development, Team Building, Stress Management, Motivational Sessions, Gamification, Energizers, and wellness initiatives such as Yoga & Meditation. These programs are designed to build confident professionals, strong leaders, cohesive teams, and a healthy workplace culture.',

    metaTitle: 'Corporate Training Programs in India | Professional Skill Training',
    metaDescription: 'Industry-focused corporate training programs in India covering leadership, technical skills, compliance & performance improvement.',

    benefits: [
      'Improved employee productivity and performance',
      'Stronger leadership and management capabilities',
      'Better communication and teamwork',
      'Reduced workplace conflict and stress',
      'Higher employee engagement and retention',
      'Positive and inclusive organizational culture'
    ],

    targetAudience: [
      'Corporate Leaders & Board Members',
      'HR & L&D Professionals',
      'Managers & Team Leaders',
      'Employees at All Levels',
      'Compliance & Risk Teams',
      'Training Coordinators'
    ],

    trainings: [
      {
        id: 'esg-brsr',
        slug: 'esg-sustainability-brsr-training',
        customPath: '/esg-training-course',
        canonicalUrl: 'https://isde.academy/esg-training-course',
        icon: TrendingUp,
        title: 'ESG & Sustainability (BRSR Compliance)',
        shortDescription: 'Master ESG principles and BRSR reporting mandated by SEBI',
        duration: '2-5 days',
        level: 'Management to Professional',
        description: 'Comprehensive ESG training covering Business Responsibility and Sustainability Reporting (BRSR) framework mandated by SEBI for top-listed companies. Learn global ESG frameworks and sustainable business practices.',

        keyTopics: [
          'ESG Fundamentals & Importance',
          'BRSR Core Framework (SEBI Mandate)',
          'Global ESG Frameworks (GRI, SASB, TCFD)',
          'Environmental Impact Assessment',
          'Social Responsibility & Human Rights',
          'Corporate Governance Best Practices',
          'ESG Data Collection & Reporting',
          'Sustainability Strategy Development'
        ],

        learningOutcomes: [
          'Understand ESG principles and frameworks',
          'Prepare BRSR reports (SEBI compliant)',
          'Implement sustainability strategies',
          'Measure ESG performance',
          'Attract ESG investors'
        ],

        certification: 'ESG Professional Certificate',
        deliveryMode: ['Online', 'Classroom', 'Corporate'],
        mandatoryFor: 'Top 1000 listed companies (SEBI)',
        frameworks: ['BRSR', 'GRI', 'SASB', 'TCFD', 'UN SDGs'],

        metaTitle: 'ESG & BRSR Training in India | Sustainability Reporting SEBI Compliance',
        metaDescription: 'ESG and sustainability training with BRSR compliance for SEBI mandate. Learn GRI, SASB, TCFD frameworks. Master sustainability reporting and ESG strategy.',
        metaKeywords: 'ESG training india, BRSR compliance, sustainability reporting, SEBI ESG, corporate sustainability'
      },

      {
        id: 'iso-37001-abms',
        slug: 'iso-37001-anti-bribery-management-training',
        icon: Shield,
        title: 'ISO 37001 - Anti-Bribery Management System',
        shortDescription: 'Implement anti-bribery controls and compliance programs',
        duration: '1-3 days',
        level: 'Awareness to Lead Auditor',
        description: 'ISO 37001 training to implement Anti-Bribery Management Systems. Essential for organizations committed to ethical business practices and compliance with anti-corruption laws.',

        keyTopics: [
          'ISO 37001:2016 Requirements',
          'Bribery Risk Assessment',
          'Due Diligence Procedures',
          'Anti-Bribery Policies & Controls',
          'Training & Awareness Programs',
          'Reporting & Investigation',
          'Compliance with Prevention of Corruption Act',
          'Internal Auditing for ABMS'
        ],

        learningOutcomes: [
          'Implement ISO 37001 ABMS',
          'Prevent bribery and corruption',
          'Conduct bribery risk assessments',
          'Achieve ISO 37001 certification',
          'Ensure legal compliance'
        ],

        certificationLevels: ['Awareness', 'Internal Auditor', 'Lead Auditor'],
        deliveryMode: ['Classroom', 'Online', 'Corporate'],
        legalContext: 'Prevention of Corruption Act, 1988',

        metaTitle: 'ISO 37001 Anti-Bribery Management Training in India | ABMS Certification',
        metaDescription: 'ISO 37001 Anti-Bribery Management System training. Implement corruption prevention controls. Learn risk assessment, compliance. Lead Auditor certification available.',
        metaKeywords: 'ISO 37001 training india, anti-bribery management, corruption prevention, ABMS certification'
      },

      {
        id: 'enterprise-risk-management',
        slug: 'enterprise-risk-management-erm-training',
        icon: Target,
        title: 'Enterprise Risk Management (ERM)',
        shortDescription: 'Comprehensive risk identification, assessment, and mitigation',
        duration: '3-5 days',
        level: 'Management & Risk Professionals',
        description: 'Master enterprise risk management frameworks including COSO ERM and ISO 31000. Learn to identify, assess, and mitigate strategic, operational, financial, and compliance risks.',

        keyTopics: [
          'ERM Frameworks (COSO, ISO 31000)',
          'Risk Identification & Assessment',
          'Risk Appetite & Tolerance',
          'Risk Mitigation Strategies',
          'Business Continuity Planning',
          'Crisis Management',
          'Risk Monitoring & Reporting',
          'Board-Level Risk Governance'
        ],

        learningOutcomes: [
          'Implement enterprise risk management',
          'Assess and prioritize risks',
          'Develop risk mitigation plans',
          'Enhance business resilience',
          'Improve decision-making'
        ],

        certification: 'ERM Professional Certificate',
        deliveryMode: ['Classroom', 'Online', 'Corporate'],
        frameworks: ['COSO ERM', 'ISO 31000'],

        metaTitle: 'Enterprise Risk Management (ERM) Training in India | ISO 31000 COSO',
        metaDescription: 'Enterprise Risk Management training covering COSO ERM and ISO 31000. Learn risk assessment, mitigation, crisis management. Build organizational resilience.',
        metaKeywords: 'enterprise risk management training india, ERM certification, ISO 31000, COSO ERM'
      },

      {
        id: 'corporate-governance',
        slug: 'corporate-governance-compliance-training',
        icon: CheckCircle,
        title: 'Corporate Governance & Compliance',
        shortDescription: 'Board governance, regulatory compliance, and ethical business practices',
        duration: '2-3 days',
        level: 'Board Members & Senior Management',
        description: 'Comprehensive training in corporate governance principles, board responsibilities, and regulatory compliance under Companies Act 2013 and SEBI regulations.',

        keyTopics: [
          'Corporate Governance Principles',
          'Board Roles & Responsibilities',
          'Companies Act 2013 Compliance',
          'SEBI (LODR) Regulations',
          'Audit Committee Functions',
          'Related Party Transactions',
          'Whistle Blower Mechanisms',
          'Directors Duties & Liabilities'
        ],

        learningOutcomes: [
          'Understand governance frameworks',
          'Ensure regulatory compliance',
          'Discharge board responsibilities',
          'Mitigate governance risks',
          'Enhance stakeholder confidence'
        ],

        deliveryMode: ['Classroom', 'Online'],
        regulatoryContext: ['Companies Act 2013', 'SEBI LODR'],
        targetAudience: ['Board Members', 'Independent Directors', 'Company Secretaries'],

        metaTitle: 'Corporate Governance Training in India | Companies Act SEBI Compliance',
        metaDescription: 'Corporate governance and compliance training for board members and senior management. Learn Companies Act 2013, SEBI LODR regulations, board responsibilities.',
        metaKeywords: 'corporate governance training india, companies act compliance, SEBI regulations, board training'
      },

      {
        id: 'iso-26000-csr',
        slug: 'iso-26000-social-responsibility-csr-training',
        icon: Users,
        title: 'ISO 26000 & Corporate Social Responsibility',
        shortDescription: 'Implement social responsibility and sustainable business practices',
        duration: '2-3 days',
        level: 'CSR Teams & Management',
        description: 'Learn ISO 26000 guidelines on social responsibility and CSR implementation under Companies Act 2013. Build sustainable and socially responsible business practices.',

        keyTopics: [
          'ISO 26000 Principles & Core Subjects',
          'CSR Under Companies Act 2013',
          'Stakeholder Engagement',
          'Community Development',
          'Environmental Responsibility',
          'Fair Operating Practices',
          'Human Rights & Labor Practices',
          'CSR Reporting & Impact Measurement'
        ],

        learningOutcomes: [
          'Implement ISO 26000 guidelines',
          'Develop effective CSR programs',
          'Engage stakeholders meaningfully',
          'Measure social impact',
          'Ensure CSR compliance'
        ],

        deliveryMode: ['Classroom', 'Online', 'Corporate'],
        legalRequirement: 'CSR under Section 135, Companies Act 2013',

        metaTitle: 'ISO 26000 & CSR Training in India | Corporate Social Responsibility',
        metaDescription: 'ISO 26000 social responsibility and CSR training. Learn Companies Act 2013 CSR compliance, stakeholder engagement, impact measurement. Build sustainable business.',
        metaKeywords: 'ISO 26000 training india, CSR training, corporate social responsibility, companies act CSR'
      }
    ]
  },

  {
    id: 'supply-chain-logistics',
    slug: 'supply-chain-logistics',
    icon: Truck,
    name: 'Supply Chain & Logistics',
    title: 'Supply Chain & Logistics Training Programs',
    description: 'Comprehensive training in supply chain management, warehouse operations, and logistics optimization',
    heroImage: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&q=80',
    overview: 'Transform your supply chain operations with training in logistics management, warehouse optimization, lean supply chain, and security management systems. Our programs combine traditional logistics knowledge with emerging technologies and sustainability practices.',

    benefits: [
      'Optimize supply chain operations',
      'Reduce logistics costs',
      'Improve inventory management',
      'Enhance warehouse efficiency',
      'Ensure supply chain security',
      'Build resilient supply networks'
    ],

    targetAudience: [
      'Supply Chain Managers',
      'Logistics Coordinators',
      'Warehouse Managers',
      'Procurement Professionals',
      'Distribution Managers',
      'Operations Managers'
    ],

    trainings: [
      {
        id: 'logistics-scm',
        slug: 'logistics-supply-chain-management-training',
        icon: Truck,
        title: 'Logistics & Supply Chain Management',
        shortDescription: 'Comprehensive training in end-to-end supply chain operations',
        duration: '5-10 days',
        level: 'Foundation to Advanced',
        description: 'Industry-certified program covering transportation management, strategic sourcing, warehousing, inventory control, and supply chain strategy. Recognized by CII Institute of Logistics.',

        keyTopics: [
          'Supply Chain Strategy & Design',
          'Demand Planning & Forecasting',
          'Strategic Sourcing & Procurement',
          'Transportation Management',
          'Warehouse & Distribution Management',
          'Inventory Optimization',
          'Supply Chain Analytics',
          'Performance Metrics & KPIs'
        ],

        learningOutcomes: [
          'Design efficient supply chain networks',
          'Optimize inventory and logistics costs',
          'Implement demand planning',
          'Manage end-to-end supply chain',
          'Earn industry-recognized certification'
        ],

        certification: 'Supply Chain Management Professional (CII)',
        deliveryMode: ['Classroom', 'Online', 'Hybrid'],
        recognizedBy: 'CII Institute of Logistics',

        metaTitle: 'Logistics & Supply Chain Management Training in India | SCM Certification',
        metaDescription: 'Comprehensive logistics and supply chain management training. CII certified program covering warehousing, transportation, inventory management, procurement.',
        metaKeywords: 'logistics training india, supply chain management certification, SCM training, CII logistics'
      },

      {
        id: 'warehouse-management',
        slug: 'warehouse-management-optimization-training',
        icon: Factory,
        title: 'Warehouse Management & Optimization',
        shortDescription: 'Modern warehouse operations and automation technologies',
        duration: '3-5 days',
        level: 'Warehouse Professionals',
        description: 'Master warehouse operations including layout design, inventory control, automated warehousing, robotics, and performance optimization for modern distribution centers.',

        keyTopics: [
          'Warehouse Design & Layout',
          'Receiving & Put-Away Operations',
          'Picking & Packing Strategies',
          'Inventory Control & Accuracy',
          'Automated Warehousing & Robotics',
          'Warehouse Management Systems (WMS)',
          'Safety & Housekeeping (5S)',
          'Performance Measurement & KPIs'
        ],

        learningOutcomes: [
          'Design efficient warehouse layouts',
          'Implement optimal picking strategies',
          'Use warehouse management systems',
          'Improve inventory accuracy',
          'Enhance warehouse productivity'
        ],

        certification: 'Certified Warehouse Logistics Professional (CWLP)',
        deliveryMode: ['Classroom', 'On-Site', 'Hybrid'],

        metaTitle: 'Warehouse Management Training in India | WMS & Automation Certification',
        metaDescription: 'Warehouse management and optimization training. Learn WMS, automated warehousing, robotics, inventory control. CWLP certification for warehouse professionals.',
        metaKeywords: 'warehouse management training india, WMS training, warehouse automation, CWLP certification'
      },

      {
        id: 'lean-supply-chain',
        slug: 'lean-supply-chain-management-training',
        icon: TrendingUp,
        title: 'Lean Supply Chain Management',
        shortDescription: 'Eliminate waste and optimize supply chain processes',
        duration: '3-5 days',
        level: 'Intermediate to Advanced',
        description: 'Apply lean principles to supply chain operations. Learn value stream mapping, waste elimination, and continuous improvement for supply chains.',

        keyTopics: [
          'Lean Principles in Supply Chain',
          'Value Stream Mapping for Supply Chain',
          'Waste Identification & Elimination',
          'Just-in-Time (JIT) Inventory',
          'Kanban Systems',
          'Supplier Collaboration',
          'Continuous Flow & Pull Systems',
          'Supply Chain Kaizen'
        ],

        learningOutcomes: [
          'Apply lean principles to supply chain',
          'Map and optimize value streams',
          'Reduce supply chain waste',
          'Implement JIT systems',
          'Improve supplier collaboration'
        ],

        certification: 'Lean Supply Chain Certification',
        deliveryMode: ['Classroom', 'Corporate', 'Hybrid'],

        metaTitle: 'Lean Supply Chain Management Training in India | VSM JIT Kanban',
        metaDescription: 'Lean supply chain management training. Learn value stream mapping, JIT, Kanban, waste elimination. Optimize logistics and reduce costs with lean principles.',
        metaKeywords: 'lean supply chain training india, value stream mapping, JIT training, lean logistics'
      },

      {
        id: 'iso-28000',
        slug: 'iso-28000-supply-chain-security-training',
        icon: Shield,
        title: 'ISO 28000 - Supply Chain Security Management',
        shortDescription: 'Security management systems for resilient supply chains',
        duration: '2-3 days',
        level: 'Security & Compliance Professionals',
        description: 'Implement ISO 28000 security management for supply chains. Essential for manufacturing, ports, transport, and warehousing operations to enhance resilience and security governance.',

        keyTopics: [
          'ISO 28000 Requirements',
          'Supply Chain Security Risk Assessment',
          'Physical & Personnel Security',
          'Cargo & Container Security',
          'Supplier Security Evaluation',
          'Security Incident Management',
          'Business Continuity in Supply Chain',
          'Compliance with Customs & Trade Security'
        ],

        learningOutcomes: [
          'Implement ISO 28000 security system',
          'Assess supply chain security risks',
          'Enhance supply chain resilience',
          'Achieve ISO 28000 certification',
          'Meet trade security requirements'
        ],

        certificationLevels: ['Awareness', 'Internal Auditor', 'Lead Auditor'],
        deliveryMode: ['Online', 'Classroom'],
        applicableTo: ['Manufacturing', 'Ports', 'Transport', 'Warehousing'],

        metaTitle: 'ISO 28000 Supply Chain Security Training in India | SCMS Certification',
        metaDescription: 'ISO 28000 supply chain security management training. Learn security risk assessment, cargo security, resilience. Certification for logistics and transport professionals.',
        metaKeywords: 'ISO 28000 training india, supply chain security, logistics security management'
      },

      {
        id: 'inventory-management',
        slug: 'inventory-management-optimization-training',
        icon: Target,
        title: 'Inventory Management & Optimization',
        shortDescription: 'Advanced inventory control and optimization techniques',
        duration: '2-3 days',
        level: 'All Supply Chain Professionals',
        description: 'Master inventory management techniques including ABC analysis, EOQ, safety stock, and inventory optimization strategies to balance costs and service levels.',

        keyTopics: [
          'Inventory Classification (ABC, XYZ)',
          'Economic Order Quantity (EOQ)',
          'Safety Stock & Reorder Point',
          'Demand Forecasting',
          'Inventory Turnover Analysis',
          'Cycle Counting',
          'Obsolete Inventory Management',
          'Inventory Optimization Tools'
        ],

        learningOutcomes: [
          'Classify and prioritize inventory',
          'Calculate optimal order quantities',
          'Reduce inventory carrying costs',
          'Improve inventory accuracy',
          'Balance costs and service levels'
        ],

        deliveryMode: ['Classroom', 'Online', 'Corporate'],
        toolsCovered: ['Excel', 'Inventory Optimization Software'],

        metaTitle: 'Inventory Management Training in India | EOQ Safety Stock Optimization',
        metaDescription: 'Inventory management and optimization training. Learn ABC analysis, EOQ, safety stock, demand forecasting. Reduce costs while maintaining service levels.',
        metaKeywords: 'inventory management training india, inventory optimization, EOQ training, inventory control'
      },

      {
        id: 'sap-scm',
        slug: 'sap-supply-chain-management-training',
        icon: Monitor,
        title: 'SAP Supply Chain Management',
        shortDescription: 'Master SAP modules for supply chain operations',
        duration: '5-10 days',
        level: 'SAP Users & Professionals',
        description: 'Comprehensive training in SAP modules for supply chain including MM (Materials Management), SD (Sales & Distribution), and WM (Warehouse Management).',

        keyTopics: [
          'SAP MM (Materials Management)',
          'SAP SD (Sales & Distribution)',
          'SAP WM (Warehouse Management)',
          'Purchase Order Processing',
          'Inventory Management in SAP',
          'Goods Receipt & Invoice Verification',
          'MRP & Production Planning',
          'SAP Integration & Reporting'
        ],

        learningOutcomes: [
          'Navigate SAP supply chain modules',
          'Process purchase orders in SAP',
          'Manage inventory in SAP',
          'Generate supply chain reports',
          'Optimize SAP for logistics'
        ],

        certification: 'SAP Supply Chain Certification',
        deliveryMode: ['Classroom', 'Online'],
        modules: ['SAP MM', 'SAP SD', 'SAP WM'],

        metaTitle: 'SAP Supply Chain Management Training in India | SAP MM SD WM',
        metaDescription: 'SAP supply chain management training covering MM, SD, WM modules. Learn purchase orders, inventory, warehouse management in SAP. Certification available.',
        metaKeywords: 'SAP SCM training india, SAP MM training, SAP SD WM, supply chain software'
      }
    ]
  }
];

// Helper function to get industry by slug
export const getIndustryBySlug = (slug) => {
  return industryTrainings.find(industry => industry.slug === slug);
};

// Helper function to get training by slug within an industry
export const getTrainingBySlug = (industrySlug, trainingSlug) => {
  const industry = getIndustryBySlug(industrySlug);
  if (!industry) return null;
  return industry.trainings.find(training => training.slug === trainingSlug);
};

// Helper function to get all training programs across all industries
export const getAllTrainings = () => {
  return industryTrainings.flatMap(industry =>
    industry.trainings.map(training => ({
      ...training,
      industry: {
        id: industry.id,
        slug: industry.slug,
        name: industry.name
      }
    }))
  );
};
